/**
 * THE UNCLOSED-DAY PREDICATE, stated once for both packages.
 *
 * `attendanceOf` (this package) and `isUnclosedDay` (`api/utils/payableDay.ts`)
 * each carried their own copy of this test, and the api copy is the one the
 * money artefacts read. Two copies of "was this day ever closed" is how a
 * screen and a payslip come to disagree about the same day, so the rule lives
 * here (the api can import shared; shared cannot import the api) and
 * `payableDay.ts` re-exports it with its stricter input type. The reasoning
 * for the rule itself is written down there.
 */

/** The stored `DayStatus` for "checked in, never checked out". */
export const UNCLOSED_DAY_STATUS = 'INCOMPLETE';

/** The three day-level columns the predicate reads. */
export interface UnclosedDayFacts {
  status: string | null | undefined;
  /** Explicit `null` is the midnight-close signature; `undefined` is unread. */
  lastPunchOut?: Date | string | null;
  totalWorkedMinutes?: number | null;
}

/**
 * True when this day was never checked out and so cannot be priced.
 *
 * "Never checked out" means the day ENDS on a check-in, not merely that it has
 * no check-out at all (audit MD-01). The rollup states that fact once, in the
 * column this reads: `lastPunchOut` is stored NULL whenever the day's last work
 * leg leaves the worker on the clock, so IN 07:00 → OUT 12:00 → IN 13:00 is
 * unclosed even on a rest day or a holiday, which the status ladder files as
 * WEEKEND / HOLIDAY above INCOMPLETE. Before that, the first pair's OUT stood
 * in this column and the trailing IN was paid to midnight as premium OT2.
 *
 * `undefined` is NOT `null`: `null` is a read column that came back empty,
 * `undefined` is a column nobody read, and unknown must not become unpaid.
 * Fails OPEN on a missing row for the same reason.
 */
export function dayIsUnclosed(day: UnclosedDayFacts | null | undefined): boolean {
  if (!day) return false;
  if (day.status === UNCLOSED_DAY_STATUS) return true;
  return day.lastPunchOut === null && (day.totalWorkedMinutes ?? 0) > 0;
}
